import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

const META_PIXEL_ID = '1750744309253921';
const STORAGE_KEY = 'sami_cookie_consent_v1';

type ConsentStatus = 'accepted' | 'rejected';

interface MetaPixelFunction {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: MetaPixelFunction;
  loaded: boolean;
  version: string;
}

declare global {
  interface Window {
    fbq?: MetaPixelFunction;
    _fbq?: MetaPixelFunction;
  }
}

@Injectable({ providedIn: 'root' })
export class MetaPixelService {
  private initialized = false;

  constructor(@Inject(PLATFORM_ID) private platformId: object) {}

  get isAccepted(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    return localStorage.getItem(STORAGE_KEY) === 'accepted';
  }

  get hasChoice(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    const saved = localStorage.getItem(STORAGE_KEY) as ConsentStatus | null;
    return saved === 'accepted' || saved === 'rejected';
  }

  accept(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    localStorage.setItem(STORAGE_KEY, 'accepted');
    this.load(true);
  }

  reject(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    localStorage.setItem(STORAGE_KEY, 'rejected');
    this.remove();
  }

  initFromSavedConsent(): void {
    if (!this.isAccepted) return;
    this.load(true);
  }

  trackPageView(): void {
    if (!this.isAccepted) return;
    this.load(false);
    window.fbq?.('track', 'PageView');
  }

  trackLead(eventId: string): void {
    if (!this.isAccepted) return;
    this.load(false);
    window.fbq?.(
      'track',
      'Lead',
      {
        content_name: 'Richiesta preventivo',
        content_category: 'Preventivo pulizie',
      },
      { eventID: eventId },
    );
  }

  private load(trackInitialPageView: boolean): void {
    if (!isPlatformBrowser(this.platformId)) return;

    if (!window.fbq) {
      const fbq = function (...args: unknown[]) {
        if (fbq.callMethod) {
          fbq.callMethod(...args);
        } else {
          fbq.queue.push(args);
        }
      } as MetaPixelFunction;

      window.fbq = fbq;
      window._fbq = fbq;
      fbq.push = fbq;
      fbq.loaded = true;
      fbq.version = '2.0';
      fbq.queue = [];
    }

    if (!document.querySelector('script[src="https://connect.facebook.net/en_US/fbevents.js"]')) {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(script);
    }

    if (!this.initialized) {
      window.fbq('init', META_PIXEL_ID);
      this.initialized = true;
    }

    if (trackInitialPageView) {
      window.fbq('track', 'PageView');
    }
  }

  private remove(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    document
      .querySelectorAll('script[src="https://connect.facebook.net/en_US/fbevents.js"]')
      .forEach(script => script.remove());
    ['_fbp', '_fbc'].forEach(name => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.samipulizie.it`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
    this.initialized = false;
  }
}
